import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import apiFetch from '../apiFetch';

const EnhancedHealthDashboard = ({ data, darkMode = false, detailsPending = false, onRequestFullData }) => {
  const [selectedSite, setSelectedSite] = useState('all');
  const [exporting, setExporting] = useState('');   // 'excel' | 'word' while downloading
  const [selectedDayFilter, setSelectedDayFilter] = useState(null); // single day 'YYYY-MM-DD' or null
  const [dateRangeStart, setDateRangeStart] = useState(null); // 'YYYY-MM-DD' or null
  const [dateRangeEnd, setDateRangeEnd] = useState(null);     // 'YYYY-MM-DD' or null
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [showInverterChart, setShowInverterChart] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [weightedAvgModalData, setWeightedAvgModalData] = useState(null);
  // A cell clicked before detailed_analysis_table has arrived (dashboard opens on the
  // lightweight /latest?summary=1 payload). Replayed once the full data lands.
  const [pendingCell, setPendingCell] = useState(null); // {siteName, time} | null

  // With a single site (e.g. Belectric live, or a single-site upload) there's no
  // site picker, so "All Sites Combined" would otherwise hide the per-inverter
  // breakdown. Auto-select that one site so the full detail view is reachable.
  // For multi-site data, reset to 'all' if the selected site is no longer present.
  useEffect(() => {
    const s = Array.isArray(data?.sites) ? data.sites : [];
    if (s.length === 1) {
      setSelectedSite(s[0].site_name);
    } else if (s.length > 1) {
      setSelectedSite(prev => (prev !== 'all' && !s.some(x => x.site_name === prev)) ? 'all' : prev);
    }
  }, [data]);

  // Replay a queued cell click once the detailed rows arrive, so the modal the user
  // asked for opens by itself instead of making them click the cell a second time.
  useEffect(() => {
    if (!pendingCell) return;
    const s = Array.isArray(data?.sites) ? data.sites : [];
    const site = s.find(x => x.site_name === pendingCell.siteName);
    if (site && site.detailed_analysis_table && site.detailed_analysis_table.length > 0) {
      handleAllSitesCellClick(site, pendingCell.time);
      setPendingCell(null);
      return;
    }
    // Don't spin forever if the full-payload fetch fails or times out.
    const timer = setTimeout(() => {
      setPendingCell(null);
      setModalData(prev => (prev && prev.loading ? { ...prev, loading: false, loadFailed: true } : prev));
    }, 30000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pendingCell]);

  console.log('EnhancedHealthDashboard received data:', data);
  
  if (!data) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-semibold mb-2">
          No data received
        </div>
        <p className="text-gray-600">Please try uploading a valid Excel file.</p>
      </div>
    );
  }

  // Accept data with or without .success (e.g. from /latest result); require sites array
  const sites = Array.isArray(data.sites) ? data.sites : [];
  const overall_stats = data.overall_stats || {};

  if (sites.length === 0 && !data.error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-semibold mb-2">
          No site data available
        </div>
        <p className="text-gray-600">Upload an Excel file or check that the backend returned site data.</p>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-semibold mb-2">
          {data.error}
        </div>
        <p className="text-gray-600">Please try again or upload a valid Excel file.</p>
      </div>
    );
  }

  const getHealthColor = (ratio) => {
    if (ratio === null || ratio === undefined) return 'text-gray-500';
    if (ratio >= 90) return 'text-green-600';  // Excellent - Green
    if (ratio >= 70) return 'text-yellow-600'; // Good - Yellow
    if (ratio >= 50) return 'text-orange-600'; // Fair - Orange
    return 'text-red-600';                     // Poor - Red
  };

  const getHealthDotColor = (ratio) => {
    if (ratio === null || ratio === undefined) return '#6b7280'; // gray
    if (ratio >= 90) return '#16a34a'; // green
    if (ratio >= 70) return '#eab308'; // yellow
    if (ratio >= 50) return '#ea580c'; // orange
    return '#dc2626'; // red
  };

  /** For a detailed_analysis_table row, return formula text: (Min X% / Max Y%) x 100 = Z% */
  const getFormulaTextForRow = (row, inverterDetails) => {
    if (!inverterDetails || !inverterDetails.length) return 'N/A';
    const percentValues = inverterDetails
      .map((inv) => row[inv.name + ', Percent'])
      .filter((v) => v != null && v !== undefined && !Number.isNaN(Number(v)) && Number(v) > 0);
    if (percentValues.length === 0) return 'N/A';
    const rowMin = Math.min(...percentValues.map(Number));
    const rowMax = Math.max(...percentValues.map(Number));
    const computedRatio = Math.round((rowMin / rowMax) * 10000) / 100;
    return '(Min ' + rowMin + '% / Max ' + rowMax + '%) x 100 = ' + computedRatio + '%';
  };

  // Helper function to calculate performance difference for a site at a specific hour
  const getSitePerformanceDifference = (site, time) => {
    if (!site || !site.detailed_analysis_table || site.detailed_analysis_table.length === 0) {
      return null;
    }

    // Find the row for this time - handle both "07:00" format and timestamp strings
    const timeRow = site.detailed_analysis_table.find(row => {
      if (!row.Timestamp) return false;
      try {
        const rowTime = new Date(row.Timestamp);
        const rowHour = rowTime.getHours();
        const rowMinute = rowTime.getMinutes();
        const rowTimeStr = `${rowHour.toString().padStart(2, '0')}:${rowMinute.toString().padStart(2, '0')}`;
        return rowTimeStr === time;
      } catch (e) {
        // If timestamp parsing fails, try string matching
        if (typeof row.Timestamp === 'string' && row.Timestamp.includes(time.replace(':', ':'))) {
          return true;
        }
        return false;
      }
    });

    if (!timeRow) {
      // Debug: Log when time row is not found
      if (site.detailed_analysis_table && site.detailed_analysis_table.length > 0) {
        const sampleTime = new Date(site.detailed_analysis_table[0].Timestamp);
        const sampleTimeStr = `${sampleTime.getHours().toString().padStart(2, '0')}:${sampleTime.getMinutes().toString().padStart(2, '0')}`;
        console.log(`Time row not found for ${time}. Sample time from table: ${sampleTimeStr}`);
      }
      return null;
    }

    // Extract all inverter percentages (keys like "Inverter - INV1, Percent")
    const inverterPercentages = [];
    Object.keys(timeRow).forEach(key => {
      if (key.includes('Percent') && !key.includes('Min/Max')) {
        const value = timeRow[key];
        if (value !== null && value !== undefined && value > 0) {
          // Extract inverter name from key (e.g., "Inverter - INV1, Percent" -> "INV1")
          const invMatch = key.match(/Inverter - (INV\d+)/);
          if (invMatch) {
            inverterPercentages.push({
              inverter: invMatch[1],
              percent: value
            });
          }
        }
      }
    });

    if (inverterPercentages.length === 0) {
      console.log(`No inverter percentages found for ${site.site_name} at ${time}`);
      return null;
    }

    const maxPercent = Math.max(...inverterPercentages.map(inv => inv.percent));
    const minPercent = Math.min(...inverterPercentages.map(inv => inv.percent));
    const difference = maxPercent - minPercent;

    return {
      difference,
      maxPercent,
      minPercent,
      maxInverter: inverterPercentages.find(inv => inv.percent === maxPercent)?.inverter,
      minInverters: inverterPercentages.filter(inv => inv.percent === minPercent).map(inv => inv.inverter),
      allInverters: inverterPercentages
    };
  };


  // Entry point for an "all sites combined" cell click. On the dashboard the initial
  // payload is the /latest?summary=1 result, which carries no detailed_analysis_table —
  // so if the rows aren't here yet, show a loading modal, ask App to fetch the full
  // result, and queue this click to replay when it lands. (History always has the rows,
  // which is why the modal only ever failed on the dashboard.)
  const openAllSitesCellDetails = (site, time) => {
    if (!site) return;
    if (site.detailed_analysis_table && site.detailed_analysis_table.length > 0) {
      handleAllSitesCellClick(site, time);
      return;
    }
    if (onRequestFullData) {
      setPendingCell({ siteName: site.site_name, time });
      setModalData({
        siteName: site.site_name,
        time,
        loading: true,
        allInverters: [],
        maxPercent: null,
        bestInverter: null,
        worst2Performing: []
      });
      onRequestFullData();
    } else {
      console.log('Detailed data unavailable for', site.site_name, 'at', time);
    }
  };

  // Handle cell click for "all sites combined" - show inverters for specific site at that hour
  // Declared as a function (not a const) so the pending-click effect above can call it.
  function handleAllSitesCellClick(site, time) {
    console.log('Cell clicked for site:', site?.site_name, 'at time:', time);

    if (!site || !site.detailed_analysis_table || site.detailed_analysis_table.length === 0) {
      console.log('Site data not available');
      return;
    }

    // Find the row for this time
    const timeRow = site.detailed_analysis_table.find(row => {
      if (!row.Timestamp) return false;
      try {
        const rowTime = new Date(row.Timestamp);
        const rowHour = rowTime.getHours();
        const rowMinute = rowTime.getMinutes();
        const rowTimeStr = `${rowHour.toString().padStart(2, '0')}:${rowMinute.toString().padStart(2, '0')}`;
        return rowTimeStr === time;
      } catch (e) {
        if (typeof row.Timestamp === 'string' && row.Timestamp.includes(time.replace(':', ':'))) {
          return true;
        }
        return false;
      }
    });

    if (!timeRow) {
      console.log('Time row not found for:', time);
      console.log('Available rows:', site.detailed_analysis_table?.length);
      if (site.detailed_analysis_table && site.detailed_analysis_table.length > 0) {
        console.log('First row keys:', Object.keys(site.detailed_analysis_table[0]));
        console.log('First row timestamp:', site.detailed_analysis_table[0].Timestamp);
      }
      return;
    }

    console.log('Time row found. Keys:', Object.keys(timeRow));
    console.log('Inverter details:', site.inverter_details?.length);

    // Extract all inverter percentages for this site at this hour
    const allInvertersForHour = [];
    let inverterIndex = 1; // Start counting from 1
    
    // Use inverter_details if available to get correct column names
    if (site.inverter_details && site.inverter_details.length > 0) {
      site.inverter_details.forEach(inv => {
        // The column name format is like "Inverter Name, Percent"
        const percentKey = `${inv.name}, Percent`;
        const value = timeRow[percentKey];
        
        console.log(`Checking key: ${percentKey}, value:`, value);
        
        if (value !== null && value !== undefined && value > 0) {
          // Use sequential numbering starting from 1, not the actual number from the name
          allInvertersForHour.push({
            inverter: inverterIndex.toString(), // Sequential number: 1, 2, 3, etc.
            percent: value,
            fullName: inv.name,
            capacity_kw: inv.capacity_kw
          });
          inverterIndex++;
        }
      });
    }
    
    // Also try to find Percent columns directly as fallback
    if (allInvertersForHour.length === 0) {
      console.log('No inverters found via inverter_details, trying direct column search...');
      let fallbackIndex = 1; // Start counting from 1
      Object.keys(timeRow).forEach(key => {
        if (key.includes('Percent') && !key.includes('Min/Max') && !key.includes('Health') && !key.includes('Ratio')) {
          const value = timeRow[key];
          console.log(`Found Percent column: ${key}, value:`, value);
          if (value !== null && value !== undefined && value > 0) {
            const fullName = key.replace(', Percent', '');
            const matchedInv = site.inverter_details?.find(id => id.name === fullName);
            allInvertersForHour.push({
              inverter: fallbackIndex.toString(),
              percent: value,
              fullName,
              capacity_kw: matchedInv?.capacity_kw
            });
            fallbackIndex++;
          }
        }
      });
    }
    
    console.log('Found inverters:', allInvertersForHour.length, allInvertersForHour);

    if (allInvertersForHour.length === 0) {
      console.log('No inverters found for hour:', time);
      console.log('Available keys in timeRow:', Object.keys(timeRow));
      // Still show modal but with empty data
      setModalData({
        siteName: site.site_name,
        time,
        allInverters: [],
        maxPercent: null,
        bestInverter: null,
        worst2Performing: []
      });
      return;
    }

    // Find best performing inverter
    const percentages = allInvertersForHour.map(inv => inv.percent).filter(p => p !== null && p !== undefined);
    if (percentages.length === 0) {
      console.log('No valid percentages found');
      return;
    }
    
    const maxPercent = Math.max(...percentages);
    const bestInverter = allInvertersForHour.find(inv => inv.percent === maxPercent);

    // Sort inverters by performance (lowest first) to get up to 2 worst performers.
    // IMPORTANT: exclude the best performer(s) so the best inverter is never shown as "worst".
    const sortedInverters = [...allInvertersForHour].sort((a, b) => (a.percent || 0) - (b.percent || 0));
    const worstCandidates = sortedInverters.filter(inv => inv.percent !== maxPercent);
    const worst2Performing = worstCandidates.slice(0, 2);

    // Quotation: show when all non-best inverters are red (≥10% behind) AND best has lower capacity than all others
    const nonBestInverters = allInvertersForHour.filter(inv => inv.inverter !== bestInverter?.inverter);
    const allNonBestRed = nonBestInverters.length > 0 && nonBestInverters.every(inv => (maxPercent - (inv.percent || 0)) >= 10);
    const bestCapacity = bestInverter && (allInvertersForHour.find(inv => inv.inverter === bestInverter.inverter)?.capacity_kw);
    const othersHaveHigherCapacity = bestCapacity != null && nonBestInverters.every(inv => (inv.capacity_kw ?? 0) > bestCapacity);
    const showCapacityQuotation = allNonBestRed && othersHaveHigherCapacity;

    console.log('Setting modal data:', {
      siteName: site.site_name,
      time,
      inverterCount: allInvertersForHour.length,
      maxPercent,
      bestInverter: bestInverter?.inverter,
      worst2Count: worst2Performing.length,
      showCapacityQuotation
    });

    // Set modal data
    setModalData({
      siteName: site.site_name,
      time,
      allInverters: allInvertersForHour,
      maxPercent,
      bestInverter: bestInverter || null,
      worst2Performing: worst2Performing,
      showCapacityQuotation: !!showCapacityQuotation
    });

    console.log('Modal data set, modal should open now');
  }

  // Handle weighted average calculation view
  const handleViewWeightedAvg = (siteName) => {
    const hours = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
    
    // Find the site in the sites array
    const site = sites.find(s => {
      const cleanSiteName = s.site_name.replace(/^Site \d+: /, '');
      return cleanSiteName === siteName || s.site_name === siteName;
    });
    
    if (!site || !site.hourly_data) {
      console.error('Site not found or no hourly data:', siteName);
      return;
    }
    
    // Calculate breakdown for each hour
    const calculationBreakdown = [];
    let sumHealthPower = 0;
    let sumPower = 0;
    
    hours.forEach(hour => {
      const hourData = (site.hourly_data || []).find(h => h.time === hour);
      if (hourData) {
        const health = hourData.avg_health_ratio;
        const power = hourData.total_power_kw || 0;
        const healthPower = (health !== null && health !== undefined && power > 0) ? health * power : 0;
        
        if (power > 0) {
          sumHealthPower += healthPower;
          sumPower += power;
        }
        
        calculationBreakdown.push({
          hour,
          health: health !== null && health !== undefined ? health : null,
          power: power > 0 ? power : 0,
          healthPower: healthPower > 0 ? parseFloat(healthPower.toFixed(2)) : 0
        });
      } else {
        calculationBreakdown.push({
          hour,
          health: null,
          power: 0,
          healthPower: 0
        });
      }
    });
    
    const weightedAvg = sumPower > 0 ? parseFloat((sumHealthPower / sumPower).toFixed(2)) : null;
    
    setWeightedAvgModalData({
      siteName: site.site_name.replace(/^Site \d+: /, ''),
      calculationBreakdown,
      sumHealthPower: parseFloat(sumHealthPower.toFixed(2)),
      sumPower: parseFloat(sumPower.toFixed(2)),
      weightedAvg
    });
  };

          // Get data for selected site or all sites
          const getCurrentData = () => {
            if (selectedSite === 'all') {
              console.log('Creating separate lines for all sites:', sites.length);
              console.log('Site names:', sites.map(s => s.site_name));
              
              // Create a single timeline with data from all sites (time on X-axis, sites as lines)
              // Exclude 06:00 and 19:00 for All Sites Combined view
              const hours = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
              
              const combinedTimeline = hours.map(hour => {
                const hourData = {
                  hour: parseInt(hour.split(':')[0]),
                  time: hour,
                  working_inverters: 0,
                  total_inverters: 0
                };
                const siteValuesAtHour = [];
                // Add data for each site
                sites.forEach(site => {
                  const siteHourData = (site.hourly_data || []).find(h => h.time === hour);
                  if (siteHourData) {
                    hourData[`site_${site.site_name.replace(/[^a-zA-Z0-9]/g, '_')}`] = siteHourData.avg_health_ratio;
                    hourData.working_inverters += siteHourData.working_inverters;
                    hourData.total_inverters += siteHourData.total_inverters;
                    if (siteHourData.avg_health_ratio != null && !Number.isNaN(siteHourData.avg_health_ratio)) {
                      siteValuesAtHour.push(siteHourData.avg_health_ratio);
                    }
                  }
                });
                // One "Overall" number per hour = average of all sites' avg at this hour (so math matches one number)
                hourData.avg_health_ratio = siteValuesAtHour.length > 0
                  ? Math.round((siteValuesAtHour.reduce((a, b) => a + b, 0) / siteValuesAtHour.length) * 100) / 100
                  : null;
                return hourData;
              });
              
              console.log('Final timeline with separate site data:', combinedTimeline);
              
              // Also create transposed data for the table (sites as rows, time as columns)
              const sitesData = sites.map((site, index) => {
                // Remove only "Site X:" prefix from site name, keep NMUSD
                const cleanSiteName = site.site_name.replace(/^Site \d+: /, '');
                
                const siteData = {
                  site_name: cleanSiteName,
                  site_name_short: cleanSiteName,
                  active_inverters: 0  // Maximum working inverters across all hours
                };
                
                let maxWorkingInverters = 0;
                let sumHealthPower = 0;  // Σ(Health × Power)
                let sumPower = 0;        // Σ(Power)
                
                // Debug: Check first site's hourly data structure
                if (index === 0 && site.hourly_data && site.hourly_data.length > 0) {
                  console.log('Sample hourly_data entry:', site.hourly_data[0]);
                  console.log('Has total_power_kw?', 'total_power_kw' in (site.hourly_data[0] || {}));
                }
                
                hours.forEach(hour => {
                  const siteHourData = (site.hourly_data || []).find(h => h.time === hour);
                  if (siteHourData) {
                    siteData[`time_${hour.replace(':', '_')}`] = siteHourData.avg_health_ratio;
                    // Track maximum working inverters across all hours
                    if (siteHourData.working_inverters > maxWorkingInverters) {
                      maxWorkingInverters = siteHourData.working_inverters;
                    }
                    
                    // Calculate weighted average components
                    const health = siteHourData.avg_health_ratio;
                    const power = siteHourData.total_power_kw || 0;
                    
                    // Debug: Log first site's first hour data
                    if (index === 0 && hour === '07:00') {
                      console.log(`Weighted avg calculation for ${site.site_name} at ${hour}:`, {
                        health,
                        power,
                        total_power_kw: siteHourData.total_power_kw,
                        hasTotalPowerKw: 'total_power_kw' in siteHourData
                      });
                    }
                    
                    if (health !== null && health !== undefined && power > 0) {
                      sumHealthPower += health * power;
                      sumPower += power;
                    }
                  } else {
                    siteData[`time_${hour.replace(':', '_')}`] = null;
                  }
                });
                
                siteData.active_inverters = maxWorkingInverters;
                // Add total inverters from site stats
                siteData.total_inverters = site.site_stats?.total_inverters || 0;
                
                // Calculate weighted average health: Σ(Health × Power) / Σ(Power)
                if (sumPower > 0) {
                  siteData.weighted_avg_health = parseFloat((sumHealthPower / sumPower).toFixed(2));
                } else {
                  siteData.weighted_avg_health = null;
                }
                
                // Debug: Log calculation result for first site
                if (index === 0) {
                  console.log(`Weighted avg result for ${site.site_name}:`, {
                    sumHealthPower,
                    sumPower,
                    weighted_avg_health: siteData.weighted_avg_health
                  });
                }
                
                return siteData;
              });
              
              return {
                hourly_data: combinedTimeline,  // For chart: time on X-axis
                sites_table_data: sitesData,    // For table: sites as rows, time as columns
                site_stats: overall_stats,
                site_name: 'Sites Combined'
              };
            } else {
              const site = sites.find(s => s.site_name === selectedSite);
              if (site) {
                // Remove only "Site X:" prefix from site name, keep NMUSD
                const cleanSiteName = site.site_name.replace(/^Site \d+: /, '');
                
                // For individual sites, create inverter-based chart and table data
                const hours = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
                
                // Get inverter details from the site data
                const inverterDetails = site.inverter_details || [];
                
                // Helper: get rows for this hour (optionally filtered by single day or date range)
                const getRowsForHour = (hourNum) => {
                  if (!site.detailed_analysis_table) return [];
                  return site.detailed_analysis_table.filter(row => {
                    if (!row.Timestamp) return false;
                    let rowTime;
                    if (typeof row.Timestamp === 'string') {
                      rowTime = new Date(row.Timestamp);
                    } else {
                      rowTime = row.Timestamp;
                    }
                    if (rowTime.getHours() !== hourNum) return false;
                    const rowDate = rowTime.toISOString().slice(0, 10);
                    if (selectedDayFilter) {
                      if (rowDate !== selectedDayFilter) return false;
                    } else if (dateRangeStart && dateRangeEnd) {
                      if (rowDate < dateRangeStart || rowDate > dateRangeEnd) return false;
                    }
                    return true;
                  });
                };
                
                // Create chart data: time on X-axis, each inverter as a line with percentage values
                // For multi-day: use average of all rows at that hour; for single day: same (one row)
                const inverterChartData = hours.map(hour => {
                  const hourData = { time: hour };
                  const hourNum = parseInt(hour.split(':')[0]);
                  const rowsAtHour = getRowsForHour(hourNum);
                  
                  inverterDetails.forEach(inv => {
                    const percentKey = `${inv.name}, Percent`;
                    const values = rowsAtHour
                      .map(r => r[percentKey])
                      .filter(v => v !== undefined && v !== null && !Number.isNaN(Number(v)));
                    const avg = values.length > 0 ? values.reduce((a, b) => a + Number(b), 0) / values.length : null;
                    hourData[inv.name] = avg !== null ? Math.round(avg * 100) / 100 : null;
                  });
                  
                  return hourData;
                });
                
                // Create table data: inverters as rows, time as columns (average across all days at that hour, or single day when filtered)
                const inverterTableData = inverterDetails.map(inv => {
                  const invRow = {
                    inverter_name: inv.name,
                    capacity_kw: inv.capacity_kw
                  };
                  
                  hours.forEach(hour => {
                    const hourNum = parseInt(hour.split(':')[0]);
                    const rowsAtHour = getRowsForHour(hourNum);
                    const percentKey = `${inv.name}, Percent`;
                    const values = rowsAtHour
                      .map(r => r[percentKey])
                      .filter(v => v !== undefined && v !== null && !Number.isNaN(Number(v)));
                    const avg = values.length > 0 ? values.reduce((a, b) => a + Number(b), 0) / values.length : null;
                    invRow[`time_${hour.replace(':', '_')}`] = avg !== null ? Math.round(avg * 100) / 100 : null;
                  });
                  
                  return invRow;
                });
                
                // Unique dates in this site's data (for day picker when multi-day)
                let availableDates = [];
                if (site.detailed_analysis_table && site.detailed_analysis_table.length > 0) {
                  const dateSet = new Set();
                  site.detailed_analysis_table.forEach(row => {
                    if (!row.Timestamp) return;
                    const t = typeof row.Timestamp === 'string' ? new Date(row.Timestamp) : row.Timestamp;
                    dateSet.add(t.toISOString().slice(0, 10));
                  });
                  availableDates = Array.from(dateSet).sort();
                }
                
                return {
                  ...site,
                  site_name: cleanSiteName,
                  inverter_chart_data: inverterChartData,
                  inverter_table_data: inverterTableData,
                  available_dates: availableDates,
                  is_multi_day: site.site_stats?.is_multi_day
                };
              }
              // Selected site not present in the current dataset (e.g. transient
              // state while the progressive live loader swaps datasets). Return a
              // fully-shaped empty object so nothing downstream maps over undefined.
              return {
                hourly_data: [],
                site_stats: {},
                site_name: selectedSite.replace(/^Site \d+: /, ''),
                inverter_chart_data: [],
                inverter_table_data: [],
                inverter_details: [],
                detailed_analysis_table: [],
                detailed_calculations: [],
                inverter_columns: [],
                available_dates: [],
              };
            }
          };

  // Handle site selection change — reset date filters when switching sites
  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
    setSelectedDayFilter(null);
    setDateRangeStart(null);
    setDateRangeEnd(null);
  };

  // Download a Word/Excel report of the current scope. When "All Sites Combined"
  // is selected we export the overview + every site ("all of it"); when a single
  // site is selected we export just that site. The backend builds the graph and
  // the full data tables from the same result payload the dashboard is showing.
  const handleDownload = async (fmt) => {
    if (!data || !Array.isArray(data.sites) || data.sites.length === 0) return;
    setExporting(fmt);
    try {
      const query = selectedSite && selectedSite !== 'all'
        ? `?site=${encodeURIComponent(selectedSite)}`
        : '';
      const response = await apiFetch(`/export/${fmt}${query}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const msg = await response.text().catch(() => '');
        throw new Error(`Export failed: ${response.status} ${msg}`);
      }
      // Prefer the server-provided filename from Content-Disposition.
      const disp = response.headers.get('Content-Disposition') || '';
      const match = disp.match(/filename="?([^"]+)"?/);
      const fallback = `VoltIQ_${selectedSite && selectedSite !== 'all' ? 'site' : 'AllSites'}.${fmt === 'excel' ? 'xlsx' : 'docx'}`;
      const filename = match ? match[1] : fallback;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert(err.message || 'Could not generate the report. Please try again.');
    } finally {
      setExporting('');
    }
  };

  const currentData = getCurrentData();
  const { hourly_data, site_stats = {}, site_name, inverter_chart_data, inverter_table_data, inverter_details } = currentData;
  
  // For individual sites, use inverter chart data; for all sites, use hourly_data
  const chartData = selectedSite === 'all' ? hourly_data : (inverter_chart_data || hourly_data);

  return (
    <div className="space-y-8">
      {/* Site Selection - dropdown + quick-switch chips (always visible, sticky on mobile) */}
      {sites && sites.length > 1 && (
        <div className={`rounded-lg shadow-sm p-4 md:p-6 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
          <div className="flex flex-wrap justify-between items-center mb-3 md:mb-4 gap-2">
            <h3 className={`text-base md:text-lg font-semibold ${darkMode ? 'text-green-300' : 'text-green-800'}`}>Select Site</h3>
            <div className="flex items-center gap-2">
              {selectedSite !== 'all' && (
                <button
                  onClick={() => {
                    setSelectedSite('all');
                    setSelectedDayFilter(null);
                    setDateRangeStart(null);
                    setDateRangeEnd(null);
                  }}
                  className="inline-flex items-center text-xs md:text-sm font-medium px-3 py-1.5 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors"
                  title="Back to All Sites Combined"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  All Sites Combined
                </button>
              )}
              <div className="text-xs md:text-sm font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">
                Total Sites: {sites.length}
              </div>
            </div>
          </div>

          {/* Quick-switch chips: All Sites Combined + each site. Horizontally scrollable on mobile. */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 mb-3" style={{ WebkitOverflowScrolling: 'touch' }}>
            <button
              onClick={() => {
                setSelectedSite('all');
                setSelectedDayFilter(null);
                setDateRangeStart(null);
                setDateRangeEnd(null);
              }}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border transition-colors ${
                selectedSite === 'all'
                  ? 'bg-green-600 text-white border-green-600'
                  : darkMode
                    ? 'bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50 hover:border-green-400'
              }`}
            >
              All Sites Combined
            </button>
            {sites.map((site, index) => {
              const cleanSiteName = site.site_name.replace(/^Site \d+: /, '');
              const isActive = selectedSite === site.site_name;
              return (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedSite(site.site_name);
                    setSelectedDayFilter(null);
                    setDateRangeStart(null);
                    setDateRangeEnd(null);
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border transition-colors ${
                    isActive
                      ? 'bg-green-600 text-white border-green-600'
                      : darkMode
                        ? 'bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50 hover:border-green-400'
                  }`}
                  title={cleanSiteName}
                >
                  {cleanSiteName}
                </button>
              );
            })}
          </div>

          {/* Dropdown for accessibility / quick search */}
          <div className="max-w-md">
            <select
              value={selectedSite}
              onChange={handleSiteChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-green-300 text-gray-900'
              }`}
            >
              <option value="all">Sites Combined</option>
              {sites.map((site, index) => {
                const cleanSiteName = site.site_name.replace(/^Site \d+: /, '');
                return (
                  <option key={index} value={site.site_name}>
                    {cleanSiteName}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      )}

      {/* Detailed Analysis Button */}
      <div className={`rounded-lg shadow-sm p-6 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-green-800">Analysis Options</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleDownload('word')}
              disabled={!!exporting}
              title={selectedSite === 'all' ? 'Download a Word report for all sites' : 'Download a Word report for this site'}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                exporting ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-[#2B579A] text-white hover:bg-[#1e3f70]'
              }`}
            >
              {exporting === 'word' ? (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 6a2 2 0 012-2h8l6 6v8a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" /></svg>
              )}
              Word
            </button>
            <button
              onClick={() => handleDownload('excel')}
              disabled={!!exporting}
              title={selectedSite === 'all' ? 'Download an Excel report for all sites' : 'Download an Excel report for this site'}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                exporting ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-[#217346] text-white hover:bg-[#17532f]'
              }`}
            >
              {exporting === 'excel' ? (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M4 6a2 2 0 012-2h8l6 6v8a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" /></svg>
              )}
              Excel
            </button>
            <button
              onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {showDetailedAnalysis ? 'Hide' : 'Show'} Detailed Analysis
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {selectedSite === 'all'
            ? 'Downloads include an overview plus the graph and full data for every site.'
            : 'Downloads include the graph and full data for the selected site.'}
        </p>
        {showDetailedAnalysis && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-md font-semibold text-gray-800 mb-3">Calculation Process</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start space-x-2">
                <span className="font-semibold text-blue-600">1.</span>
                <span>Extract inverter capacity from column names (e.g., PVI36TL → 36kW, PVI-50 → 50kW)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold text-blue-600">2.</span>
                <span>Calculate percentage: (Active Power / Capacity) × 100 for each inverter</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold text-blue-600">3.</span>
                <span>For each timestamp, find min and max percentages among working inverters</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold text-blue-600">4.</span>
                <span>Calculate health ratio: (min / max) × 100</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold text-blue-600">5.</span>
                <span>Aggregate hourly data and generate site-wide statistics</span>
              </div>
            </div>
            
            {/* Show detailed calculations for selected site */}
            {selectedSite !== 'all' && (
              <div className="mt-6">
                <h5 className={`text-md font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Detailed Calculations for {selectedSite.replace(/^Site \d+: /, '')}
                </h5>
                <div className={`p-4 rounded border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                  {/* How the number in each block is calculated */}
                  <div className={`mb-4 p-4 rounded-lg border-2 ${darkMode ? 'bg-gray-600/50 border-green-600 text-gray-200' : 'bg-green-50 border-green-300 text-gray-800'}`}>
                    <h6 className={`text-sm font-bold mb-2 ${darkMode ? 'text-green-400' : 'text-green-800'}`}>
                      How the number is calculated (each block in the table below)
                    </h6>
                    <p className="text-sm mb-2">
                      For every timestamp we take <strong>all inverter percentages</strong> (Active Power ÷ Capacity × 100). Then:
                    </p>
                    <p className={`text-sm font-mono font-semibold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                      Health Ratio (Min/Max Ratio) = (Min inverter % ÷ Max inverter %) × 100
                    </p>
                    <p className="text-xs mt-2 opacity-90">
                      Example: If Inv1 = 69%, Inv2 = 83%, Inv3 = 56% → Min = 56%, Max = 83% → (56 ÷ 83) × 100 = <strong>67.47%</strong> in the block.
                    </p>
                  </div>
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4 ${darkMode ? 'text-gray-300' : ''}`}>
                    <div>
                      <span className={darkMode ? 'font-semibold text-gray-300' : 'font-semibold text-gray-700'}>Total Inverters:</span>
                      <span className="ml-2 text-blue-600">{site_stats.total_inverters}</span>
                    </div>
                    <div>
                      <span className={darkMode ? 'font-semibold text-gray-300' : 'font-semibold text-gray-700'}>Data Points:</span>
                      <span className="ml-2 text-blue-600">{site_stats.total_data_points}</span>
                    </div>
                    <div>
                      <span className={darkMode ? 'font-semibold text-gray-300' : 'font-semibold text-gray-700'}>Average Health Ratio:</span>
                      <span className="ml-2 text-blue-600">{site_stats.avg_health_ratio?.toFixed(2)}%</span>
                    </div>
                    <div>
                      <span className={darkMode ? 'font-semibold text-gray-300' : 'font-semibold text-gray-700'}>Max Health Ratio:</span>
                      <span className="ml-2 text-green-600">{site_stats.max_health_ratio?.toFixed(2)}%</span>
                    </div>
                    <div>
                      <span className={darkMode ? 'font-semibold text-gray-300' : 'font-semibold text-gray-700'}>Min Health Ratio:</span>
                      <span className="ml-2 text-red-600">{site_stats.min_health_ratio?.toFixed(2)}%</span>
                    </div>
                    <div>
                      <span className={darkMode ? 'font-semibold text-gray-300' : 'font-semibold text-gray-700'}>{site_stats?.is_multi_day ? 'Data period:' : 'Data Date:'}</span>
                      <span className="ml-2 text-gray-600">{site_stats?.data_date}</span>
                      {site_stats?.is_multi_day && site_stats?.unique_days > 1 && (
                        <span className="ml-1 text-gray-500">({site_stats.unique_days} days)</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Show inverter details */}
                  {currentData.inverter_details && currentData.inverter_details.length > 0 && (
                    <div className="mt-4">
                      <h6 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Inverter Capacity Extraction:</h6>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        {currentData.inverter_details.map((inverter, index) => (
                          <div key={index} className={darkMode ? 'bg-gray-600 p-2 rounded border border-gray-500' : 'bg-gray-50 p-2 rounded border'}>
                            <div className={darkMode ? 'font-medium text-gray-200' : 'font-medium text-gray-700'}>{inverter.name.split(',')[0]}</div>
                            <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                              Capacity: <span className="font-semibold text-blue-600">{inverter.capacity_kw}kW</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show detailed analysis table */}
                  {currentData.detailed_analysis_table && currentData.detailed_analysis_table.length > 0 && (
                    <div className="mt-4">
                      <h6 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        Detailed Analysis Table ({currentData.detailed_analysis_table.length} rows):
                      </h6>
                      {console.log('Detailed analysis table data:', currentData.detailed_analysis_table)}
                      {console.log('Inverter details:', currentData.inverter_details)}
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className={`min-w-full text-xs border ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <thead className={darkMode ? 'bg-gray-600 sticky top-0' : 'bg-gray-100 sticky top-0'}>
                            <tr>
                              <th className={`px-2 py-1 border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300'} text-left font-semibold`}>Timestamp</th>
                              {currentData.inverter_details && currentData.inverter_details.map((inverter, index) => (
                                <React.Fragment key={index}>
                                  <th className={`px-2 py-1 border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300'} text-left font-semibold`}>
                                    {inverter.name.split(',')[0]}, Active Power
                                  </th>
                                  <th className={`px-2 py-1 border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300'} text-left font-semibold`}>
                                    {inverter.name.split(',')[0]}, Percent
                                  </th>
                                  <th className={`px-2 py-1 border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300'} text-left font-semibold`}>
                                    {inverter.name.split(',')[0]}, Status
                                  </th>
                                </React.Fragment>
                              ))}
                              <th className={`px-2 py-1 border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300'} text-left font-semibold`}>Min/Max Ratio</th>
                              <th className={`px-2 py-1 border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300'} text-left font-semibold`}>How it&apos;s calculated</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentData.detailed_analysis_table.map((row, index) => {
                              const formulaText = getFormulaTextForRow(row, currentData.inverter_details);
                              return (
                              <tr key={index} className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-white') : (darkMode ? 'bg-gray-600/50' : 'bg-gray-50')}>
                                <td className={`px-2 py-1 border border-gray-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                  {row.Timestamp}
                                </td>
                                {currentData.inverter_details && currentData.inverter_details.map((inverter, invIndex) => {
                                  const powerCol = `${inverter.name}, Active Power`;
                                  const percentCol = `${inverter.name}, Percent`;
                                  const statusCol = `${inverter.name}, Status`;
                                  return (
                                    <React.Fragment key={invIndex}>
                                      <td className="px-2 py-1 border border-gray-300 text-center">
                                        {row[powerCol] !== null && row[powerCol] !== undefined ? (
                                          <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{row[powerCol]} kW</span>
                                        ) : (
                                          <span className="text-gray-400">NaN</span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1 border border-gray-300 text-center">
                                        {row[percentCol] !== null && row[percentCol] !== undefined ? (
                                          <span className="font-semibold text-blue-600">{row[percentCol]}%</span>
                                        ) : (
                                          <span className="text-gray-400">NaN</span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1 border border-gray-300 text-center">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                          row[statusCol] === 'OK' 
                                            ? (darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800')
                                            : (darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800')
                                        }`}>
                                          {row[statusCol] || 'Comm Issue'}
                                        </span>
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                                <td className="px-2 py-1 border border-gray-300 text-center">
                                  {row["Min/Max Ratio"] !== null && row["Min/Max Ratio"] !== undefined ? (
                                    <span className="font-semibold text-orange-600">{row["Min/Max Ratio"]}</span>
                                  ) : (
                                    <span className="text-gray-400">NaN</span>
                                  )}
                                </td>
                                <td className={`px-2 py-1 border border-gray-300 text-left text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} title={formulaText}>
                                  {formulaText}
                                </td>
                              </tr>
                            );})}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Show actual detailed calculations */}
                  {currentData.detailed_calculations && currentData.detailed_calculations.length > 0 && (
                    <div className="mt-4">
                      <h6 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Calculation Examples (per timestamp):</h6>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {currentData.detailed_calculations.map((calc, index) => (
                          <div key={index} className={darkMode ? 'bg-gray-600 p-3 rounded border border-gray-500 text-xs' : 'bg-gray-50 p-3 rounded border text-xs'}>
                            <div className={`font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {calc.timestamp}
                            </div>
                            <div className="space-y-1">
                              <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                                <span className="font-medium">Inverter Percentages:</span>
                                {Object.entries(calc.inverter_percentages).map(([name, percentage]) => (
                                  <span key={name} className="ml-2">
                                    {name.split(',')[0]}: <span className="font-semibold text-blue-600">{percentage}%</span>
                                  </span>
                                ))}
                              </div>
                              <div className="flex space-x-4 text-sm">
                                <span><span className="font-medium">Min:</span> <span className="text-red-600 font-semibold">{calc.min_percentage}%</span></span>
                                <span><span className="font-medium">Max:</span> <span className="text-green-600 font-semibold">{calc.max_percentage}%</span></span>
                                <span><span className="font-medium">Health Ratio:</span> <span className="text-orange-600 font-semibold">{calc.health_ratio}%</span></span>
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                Formula: ({calc.min_percentage} / {calc.max_percentage}) × 100 = {calc.health_ratio}%
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Show sample calculations */}
            <div className="mt-6">
              <h5 className="text-md font-semibold text-gray-800 mb-3">Sample Calculation</h5>
              <div className={`p-4 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                <div className="space-y-2">
                  <p><span className="font-semibold">Example:</span> 3 inverters at 06:00 AM</p>
                  <div className="ml-4 space-y-1 text-gray-600">
                    <p>• Inverter 1: 25kW active power / 36kW capacity = 69.4%</p>
                    <p>• Inverter 2: 30kW active power / 36kW capacity = 83.3%</p>
                    <p>• Inverter 3: 20kW active power / 36kW capacity = 55.6%</p>
                  </div>
                  <div className="ml-4 space-y-1">
                    <p><span className="font-semibold">Min:</span> 55.6%</p>
                    <p><span className="font-semibold">Max:</span> 83.3%</p>
                    <p><span className="font-semibold">Health Ratio:</span> (55.6 / 83.3) × 100 = <span className="text-orange-600 font-semibold">66.7%</span></p>
                    <p><span className="font-semibold">Status:</span> <span className="text-yellow-600 font-semibold">Good</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Site Overview */}
      <div className={`rounded-lg shadow-sm p-4 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
        <h2 className="text-lg font-bold text-green-800 mb-4">
          {site_name} Overview
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-gray-700 border-green-700 hover:border-green-500' : 'bg-white border-green-200 hover:border-green-300'}`}>
            <div className="text-sm font-medium text-green-600">{site_stats?.is_multi_day ? 'Data period (historical)' : 'Data Date'}</div>
            <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{site_stats?.data_date || 'N/A'}</div>
            {site_stats?.is_multi_day && site_stats?.unique_days > 1 && (
              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{site_stats.unique_days} days</div>
            )}
          </div>
          
          <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-gray-700 border-green-700 hover:border-green-500' : 'bg-white border-green-200 hover:border-green-300'}`}>
            <div className="text-sm font-medium text-green-600">
              {selectedSite === 'all' ? 'Total Sites' : 'Total Inverters'}
            </div>
            <div className="text-xl font-bold text-gray-900">
              {selectedSite === 'all' ? site_stats.total_sites : site_stats.total_inverters}
            </div>
          </div>
          
          <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-gray-700 border-green-700 hover:border-green-500' : 'bg-white border-green-200 hover:border-green-300'}`}>
            <div className="text-sm font-medium text-green-600">
              {selectedSite === 'all' ? 'Total Inverters' : 'Data Points'}
            </div>
            <div className="text-xl font-bold text-gray-900">
              {selectedSite === 'all' ? site_stats.total_inverters : site_stats.total_data_points}
            </div>
          </div>
          
          <div className={`rounded-lg p-3 border transition-colors ${darkMode ? 'bg-gray-700 border-green-700 hover:border-green-500' : 'bg-white border-green-200 hover:border-green-300'}`}>
            <div className="text-sm font-medium text-green-600">Avg Health Ratio</div>
            <div className={`text-xl font-bold ${getHealthColor(site_stats.avg_health_ratio)}`}>
              {site_stats.avg_health_ratio ? `${site_stats.avg_health_ratio}%` : 'N/A'}
            </div>
          </div>
        </div>
        {selectedSite === 'all' && (
          <div className={`mt-4 p-3 rounded-lg text-sm border ${darkMode ? 'bg-gray-700/50 border-gray-600 text-gray-300' : 'bg-green-50 border-green-200 text-gray-700'}`}>
            <strong>How the numbers work (all sites combined):</strong> The <strong>Avg Health Ratio</strong> above is the average of each site’s overall average (one value per site, then averaged). In the table below, each cell is that site’s average health at that hour over the full period (e.g. all 92 days at 7 AM). Multi-day data uses the same formula per site; we then combine sites by averaging those site-level numbers.
          </div>
        )}
      </div>

      {/* Health Legend - Compact - Only show for individual sites, not for "all sites combined" */}
      {selectedSite !== 'all' && (
        <div className={`rounded-lg shadow-sm p-3 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-green-800">Health Status Legend</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-200 rounded"></div>
                <span className="text-xs text-gray-700">Excellent (≥90%)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-yellow-200 rounded"></div>
                <span className="text-xs text-gray-700">Good (70-89%)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-orange-200 rounded"></div>
                <span className="text-xs text-gray-700">Fair (50-69%)</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-red-200 rounded"></div>
                <span className="text-xs text-gray-700">Poor (&lt;50%)</span>
              </div>
            </div>
          </div>
        </div>
      )}

              {/* Analytics date filters: all days, single day, or date range */}
      {selectedSite !== 'all' && currentData.available_dates && currentData.available_dates.length > 1 && (
        <div className={`rounded-lg shadow p-4 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
          <div className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Analytics filters — date range</div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="dayFilter"
                checked={!selectedDayFilter && !dateRangeStart && !dateRangeEnd}
                onChange={() => {
                  setSelectedDayFilter(null);
                  setDateRangeStart(null);
                  setDateRangeEnd(null);
                }}
                className="text-green-600"
              />
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>All days (average)</span>
            </label>
            <label className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <input
                type="radio"
                name="dayFilter"
                checked={selectedDayFilter !== null && !dateRangeStart}
                onChange={() => {
                  setDateRangeStart(null);
                  setDateRangeEnd(null);
                }}
                className="text-green-600"
              />
              <span className="mr-1">Single day:</span>
              <select
                value={selectedDayFilter || ''}
                onChange={(e) => {
                  setSelectedDayFilter(e.target.value || null);
                  setDateRangeStart(null);
                  setDateRangeEnd(null);
                }}
                className={`px-2 py-1 rounded border text-sm min-w-[140px] ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="">Select a day</option>
                {currentData.available_dates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            <label className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <input
                type="radio"
                name="dayFilter"
                checked={dateRangeStart !== null && dateRangeEnd !== null}
                onChange={() => {
                  if (!dateRangeStart || !dateRangeEnd) {
                    const first = currentData.available_dates[0];
                    const last = currentData.available_dates[currentData.available_dates.length - 1];
                    setDateRangeStart(first);
                    setDateRangeEnd(last);
                  }
                  setSelectedDayFilter(null);
                }}
                className="text-green-600"
              />
              <span className="mr-1">Date range:</span>
              <input
                type="date"
                value={dateRangeStart || ''}
                min={currentData.available_dates[0]}
                max={currentData.available_dates[currentData.available_dates.length - 1]}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setDateRangeStart(v);
                  if (v && dateRangeEnd && v > dateRangeEnd) setDateRangeEnd(v);
                  setSelectedDayFilter(null);
                }}
                className={`px-2 py-1 rounded border text-sm w-[140px] ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
              />
              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>to</span>
              <input
                type="date"
                value={dateRangeEnd || ''}
                min={dateRangeStart || currentData.available_dates[0]}
                max={currentData.available_dates[currentData.available_dates.length - 1]}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setDateRangeEnd(v);
                  if (v && dateRangeStart && v < dateRangeStart) setDateRangeStart(v);
                  setSelectedDayFilter(null);
                }}
                className={`px-2 py-1 rounded border text-sm w-[140px] ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
              />
            </label>
          </div>
          {selectedDayFilter && (
            <p className={`mt-2 text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              Showing data for <strong>{selectedDayFilter}</strong> only.
            </p>
          )}
          {dateRangeStart && dateRangeEnd && (
            <p className={`mt-2 text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              Showing data from <strong>{dateRangeStart}</strong> to <strong>{dateRangeEnd}</strong> (average over this range). Chart and table below reflect this filter.
            </p>
          )}
        </div>
      )}

              {/* Hourly Health Ratio Chart - Only show for individual sites, not for "all sites combined" */}
      {selectedSite !== 'all' && (
        <div className={`rounded-lg shadow-sm p-6 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
          <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
              Inverter Percentage Performance (7 AM - 6 PM)
              {selectedDayFilter && (
                <span className="block text-sm font-normal mt-1 opacity-90">— {selectedDayFilter}</span>
              )}
              {dateRangeStart && dateRangeEnd && !selectedDayFilter && (
                <span className="block text-sm font-normal mt-1 opacity-90">— {dateRangeStart} to {dateRangeEnd}</span>
              )}
            </h2>
            <button
              onClick={() => setShowInverterChart(!showInverterChart)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex-shrink-0"
            >
              {showInverterChart ? 'Hide Graph' : 'Show Graph'}
            </button>
          </div>
          {showInverterChart ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={chartData}
                margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {/* X-axis: Time (always) */}
                <XAxis 
                  dataKey="time" 
                  tick={{ fontSize: 12 }} 
                  angle={-45} 
                  textAnchor="end" 
                  height={60} 
                />
                {/* Y-axis: Inverter Percentage */}
                <YAxis 
                  label={{ 
                    value: 'Inverter %', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' },
                    offset: -10
                  }} 
                  tick={{ fontSize: 12 }} 
                  width={60}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (value === null || value === undefined) return ['N/A', name];
                    return [`${value}%`, name];
                  }}
                  labelFormatter={(label) => `Time: ${label}`}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      // For multiple lines, show all entries but highlight the one with the highest value
                      const validEntries = payload.filter(entry => entry.value !== null && entry.value !== undefined);
                      if (validEntries.length > 0) {
                        // Find the entry with the highest value (most likely the one being hovered)
                        const maxEntry = validEntries.reduce((max, entry) => 
                          entry.value > max.value ? entry : max
                        );
                        
                        return (
                          <div className={`p-3 border rounded-lg shadow-sm ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                            <p className="font-semibold text-gray-900">{`Time: ${label}`}</p>
                            {validEntries.map((entry, index) => (
                              <p 
                                key={index} 
                                className={`text-sm ${entry === maxEntry ? 'font-bold' : 'opacity-60'}`} 
                                style={{ color: entry.color }}
                              >
                                {`${entry.name}: ${entry.value}%`}
                              </p>
                            ))}
                          </div>
                        );
                      }
                    }
                    return null;
                  }}
                />
                {inverter_details && inverter_details.map((inv, index) => {
                  const colors = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#a855f7', '#06b6d4', '#84cc16', '#eab308', '#dc2626'];
                  const lineKey = `line-${index}-${(inv.name || '').replace(/\s/g, '_')}`;
                  return (
                    <Line
                      key={lineKey}
                      type="monotone"
                      dataKey={inv.name}
                      stroke={colors[index % colors.length]}
                      strokeWidth={2}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        if (!payload || payload[inv.name] === null || payload[inv.name] === undefined) {
                          return null;
                        }
                        const color = getHealthDotColor(payload[inv.name]);
                        return (
                          <circle
                            key={`${lineKey}-${cx}-${cy}`}
                            cx={cx}
                            cy={cy}
                            r={4}
                            fill={color}
                            stroke={color}
                            strokeWidth={2}
                          />
                        );
                      }}
                      connectNulls={false}
                      name={inv.name}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
          ) : (
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Graph hidden to keep the view clean ({inverter_details?.length || 0} inverter lines). Tap <strong>Show Graph</strong> to view it.
            </p>
          )}
        </div>
      )}

      {/* Hourly Details Table - MOVED BELOW CHART */}
      <div className={`rounded-lg shadow-sm p-6 border ${darkMode ? 'bg-gray-800 border-green-700' : 'bg-white border-green-200'}`}>
        <h2 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
          {site_name} - Hourly Details (7 AM - 6 PM)
          {selectedDayFilter && (
            <span className="block text-sm font-normal mt-1 opacity-90">— {selectedDayFilter}</span>
          )}
          {dateRangeStart && dateRangeEnd && !selectedDayFilter && (
            <span className="block text-sm font-normal mt-1 opacity-90">— {dateRangeStart} to {dateRangeEnd}</span>
          )}
          {selectedSite === 'all' && detailsPending && (
            <span className={`block text-sm font-normal mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Loading inverter-level detail — cell colours refine once it arrives.
            </span>
          )}
        </h2>
        {selectedSite !== 'all' && inverter_table_data && inverter_details && (() => {
          const hours = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
          const maxByTimeKey = {};
          hours.forEach(time => {
            const timeKey = `time_${time.replace(':', '_')}`;
            const values = inverter_table_data
              .map(inv => inv?.[timeKey])
              .filter(val => val !== null && val !== undefined && typeof val === 'number' && val > 0);
            maxByTimeKey[timeKey] = values.length ? Math.max(...values) : null;
          });
          
          let hasRedCells = false;
          let bestInverterHasLowerCapacity = false;
          let bestInverterCapacity = null;
          const bestInverterIndicesByHour = {};
          
          hours.forEach(time => {
            const timeKey = `time_${time.replace(':', '_')}`;
            const bestAtHour = maxByTimeKey[timeKey];
            if (bestAtHour !== null && bestAtHour !== undefined) {
              inverter_table_data.forEach((invRow, idx) => {
                const percentValue = invRow[timeKey];
                if (percentValue !== null && percentValue !== undefined && typeof percentValue === 'number') {
                  const diffFromBest = bestAtHour - percentValue;
                  if (diffFromBest >= 10) {
                    hasRedCells = true;
                  }
                  if (Math.abs(diffFromBest) < 1e-9 || percentValue === bestAtHour) {
                    bestInverterIndicesByHour[timeKey] = idx;
                  }
                }
              });
            }
          });
          
          if (hasRedCells && inverter_details && inverter_details.length > 0) {
            Object.values(bestInverterIndicesByHour).forEach(bestIdx => {
              if (bestIdx !== null && bestIdx !== undefined && inverter_details[bestIdx]) {
                const bestInvDetail = inverter_details[bestIdx];
                if (bestInvDetail && bestInvDetail.capacity_kw) {
                  const bestCap = bestInvDetail.capacity_kw;
                  const allOthersHigher = inverter_details.every((inv, idx) => {
                    if (idx === bestIdx) return true;
                    return !inv.capacity_kw || inv.capacity_kw >= bestCap;
                  });
                  const hasHigherCapacity = inverter_details.some((inv, idx) => 
                    idx !== bestIdx && inv.capacity_kw && inv.capacity_kw > bestCap
                  );
                  if (allOthersHigher && hasHigherCapacity) {
                    bestInverterHasLowerCapacity = true;
                    bestInverterCapacity = bestCap;
                  }
                }
              }
            });
          }
          
          return hasRedCells && bestInverterHasLowerCapacity ? (
            <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 italic">
                    <strong>Note:</strong> Some inverters appear red compared to the best performer because the best-performing inverter has a lower capacity ({bestInverterCapacity} kW) than the other inverters. Percentages are calculated relative to each inverter&apos;s own capacity, so a smaller inverter can show a higher utilization percentage even when producing less absolute power.
                  </p>
                </div>
              </div>
            </div>
          ) : null;
        })()}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-green-50">
              <tr>
                {selectedSite === 'all' ? (
                  <>
                    <th className="sticky left-0 z-20 px-3 md:px-6 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider bg-green-50" style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.08)' }}>
                      Site
                    </th>
                    {['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'].map((time, index) => (
                      <th key={index} className="px-2 md:px-4 py-3 text-center text-xs font-medium text-green-700 uppercase tracking-wider">
                        {time}
                      </th>
                    ))}
                    <th className="px-6 py-3 text-center text-xs font-medium text-green-700 uppercase tracking-wider">
                      Weighted Avg
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                      Active Inverters
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                      Inverter
                    </th>
                    {['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'].map((time, index) => (
                      <th key={index} className="px-2 md:px-4 py-3 text-center text-xs font-medium text-green-700 uppercase tracking-wider">
                        {time}
                      </th>
                    ))}
                  </>
                )}
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {selectedSite === 'all' ? (
                // Transposed: Sites as rows, Time as columns
                currentData.sites_table_data && currentData.sites_table_data.map((siteData, index) => {
                  // Find the full site object to access detailed_analysis_table
                  // Try exact match first, then try matching without "Site X:" prefix
                  let fullSite = sites.find(s => s.site_name === siteData.site_name);
                  if (!fullSite) {
                    // Try matching by removing "Site X:" prefix from both
                    const cleanSiteDataName = siteData.site_name.replace(/^Site \d+: /, '');
                    fullSite = sites.find(s => {
                      const cleanSiteName = s.site_name.replace(/^Site \d+: /, '');
                      return cleanSiteName === cleanSiteDataName;
                    });
                  }
                  
                  // Debug: Log first site to check data structure
                  if (index === 0) {
                    console.log('First site data check:', {
                      siteDataName: siteData.site_name,
                      fullSiteFound: !!fullSite,
                      fullSiteName: fullSite?.site_name,
                      hasDetailedTable: !!fullSite?.detailed_analysis_table,
                      tableLength: fullSite?.detailed_analysis_table?.length,
                      firstRowKeys: fullSite?.detailed_analysis_table?.[0] ? Object.keys(fullSite.detailed_analysis_table[0]) : null
                    });
                  }
                  
                  return (
                    <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-green-50'}>
                      <td
                        className={`sticky left-0 z-10 px-3 md:px-6 py-4 text-sm font-medium cursor-pointer transition-colors ${darkMode ? 'bg-gray-800 text-green-300 hover:bg-gray-700' : 'bg-white text-green-700 hover:bg-green-50'}`}
                        style={{ boxShadow: '2px 0 4px -2px rgba(0,0,0,0.08)' }}
                        onClick={() => {
                          if (fullSite) {
                            setSelectedSite(fullSite.site_name);
                            setSelectedDayFilter(null);
                            setDateRangeStart(null);
                            setDateRangeEnd(null);
                            if (typeof window !== 'undefined') {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }
                        }}
                        title="Open this site"
                      >
                        <span className="block max-w-[140px] md:max-w-none truncate underline decoration-dotted underline-offset-4">{siteData.site_name}</span>
                      </td>
                      {['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'].map((time, timeIndex) => {
                        const dataKey = `time_${time.replace(':', '_')}`;
                        const ratio = siteData[dataKey];
                        const perfDiff = fullSite ? getSitePerformanceDifference(fullSite, time) : null;
                        const difference = perfDiff ? perfDiff.difference : null;
                        
                        // Check if value is N/A (null or undefined)
                        const isNA = ratio === null || ratio === undefined;
                        
                        // Get color classes based on difference or N/A
                        let cellBgClass = '';
                        let textColorClass = '';
                        let borderClass = '';
                        let isClickable = false;
                        
                        if (isNA) {
                          // N/A values should be highlighted in red (not clickable)
                          cellBgClass = 'bg-red-100';
                          borderClass = 'border-2 border-red-400';
                          textColorClass = 'text-red-700';
                          isClickable = false;
                        } else if (difference !== null && difference !== undefined) {
                          if (difference <= 5) {
                            cellBgClass = 'bg-green-50';
                            textColorClass = 'text-green-700';
                            borderClass = 'border-2 border-green-300';
                            isClickable = true;
                          } else if (difference <= 10) {
                            // Orange - make clickable
                            cellBgClass = 'bg-orange-50';
                            textColorClass = 'text-orange-700';
                            borderClass = 'border-2 border-orange-400';
                            isClickable = true;
                          } else {
                            // Red - make clickable
                            cellBgClass = 'bg-red-100';
                            textColorClass = 'text-red-800';
                            borderClass = 'border-2 border-red-600';
                            isClickable = true;
                          }
                        } else {
                          // If difference calculation failed, determine color based on ratio
                          // and make orange/red cells clickable
                          if (ratio !== null && ratio !== undefined) {
                            if (ratio < 50) {
                              // Red - very poor performance
                              cellBgClass = 'bg-red-100';
                              textColorClass = 'text-red-800';
                              borderClass = 'border-2 border-red-600';
                              isClickable = true;
                            } else if (ratio < 70) {
                              // Orange - poor performance
                              cellBgClass = 'bg-orange-50';
                              textColorClass = 'text-orange-700';
                              borderClass = 'border-2 border-orange-400';
                              isClickable = true;
                            } else {
                              // Green/yellow - good performance
                              cellBgClass = 'bg-green-50';
                              borderClass = 'border-2 border-green-300';
                              textColorClass = 'text-green-700';
                              isClickable = true;
                            }
                          } else {
                            textColorClass = getHealthColor(ratio);
                            isClickable = false;
                          }
                        }
                        
                        // For All Sites Combined: allow clicking any non-N/A cell to view inverter details
                        if (!isNA && fullSite) {
                          isClickable = true;
                        }
                        
                        return (
                          <td
                            key={timeIndex}
                            className={`px-3 py-4 whitespace-nowrap text-sm text-center ${cellBgClass} ${borderClass} ${isClickable ? 'cursor-pointer hover:opacity-80 active:opacity-60 hover:shadow-md transition-all touch-manipulation' : ''}`}
                            onClick={() => {
                              if (isClickable && fullSite) {
                                openAllSitesCellDetails(fullSite, time);
                              }
                            }}
                          >
                            <span className={`font-semibold ${textColorClass}`}>
                              {ratio !== null && ratio !== undefined ? `${ratio}%` : 'N/A'}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {siteData.weighted_avg_health !== null && siteData.weighted_avg_health !== undefined 
                              ? `${siteData.weighted_avg_health}%` 
                              : 'N/A'}
                          </span>
                          {siteData.weighted_avg_health !== null && siteData.weighted_avg_health !== undefined && (
                            <button
                              onClick={() => handleViewWeightedAvg(siteData.site_name)}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              title="View calculation details"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {siteData.total_inverters > 0 
                          ? `${siteData.active_inverters}/${siteData.total_inverters}`
                          : siteData.active_inverters}
                      </td>
                    </tr>
                  );
                })
              ) : (
                // For individual sites: Inverters as rows, Time as columns
                (() => {
                  const hours = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
                  
                  // For each hour, compute the BEST (max) inverter % at that hour.
                  // Then color each inverter cell based on its difference from that best for the same hour:
                  // - orange: 5–<10 points behind best
                  // - red: ≥10 points behind best
                  const maxByTimeKey = {};
                  hours.forEach(time => {
                    const timeKey = `time_${time.replace(':', '_')}`;
                    const values = inverter_table_data
                      .map(inv => inv?.[timeKey])
                      .filter(val => val !== null && val !== undefined && typeof val === 'number' && val > 0);
                    maxByTimeKey[timeKey] = values.length ? Math.max(...values) : null;
                  });
                  
                  return inverter_table_data.map((invRow, index) => {
                    // Use sequential numbering starting from 1 (index + 1)
                    const displayName = `Inverter ${index + 1}`;
                    
                    return (
                    <tr key={index} className="hover:bg-green-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {displayName}
                      </td>
                      {hours.map((time, timeIndex) => {
                        const timeKey = `time_${time.replace(':', '_')}`;
                        const percentValue = invRow[timeKey]; // This inverter's value at this hour
                        const bestAtHour = maxByTimeKey[timeKey]; // Best inverter value for THIS HOUR across all inverters
                        
                        // Determine color based on difference from the best inverter at THIS HOUR (column-wise)
                        let bgColorClass = '';
                        let borderClass = '';
                        let textColorClass = 'text-green-700';
                        
                        // Check if value is N/A (null or undefined - 0% is a valid value, not N/A)
                        const isNA = percentValue === null || percentValue === undefined;
                        
                        if (isNA) {
                          // N/A values should be highlighted in red
                          bgColorClass = 'bg-red-100';
                          borderClass = 'border-2 border-red-400';
                          textColorClass = 'text-red-700';
                        } else if (bestAtHour !== null && bestAtHour !== undefined && typeof bestAtHour === 'number' && bestAtHour > 0 && typeof percentValue === 'number') {
                          const diffFromBest = bestAtHour - percentValue;
                          const isBest = Math.abs(diffFromBest) < 1e-9;

                          if (isBest) {
                            bgColorClass = 'bg-green-50';
                            borderClass = 'border-2 border-green-300';
                            textColorClass = 'text-green-700';
                          } else if (diffFromBest >= 10) {
                            bgColorClass = 'bg-red-100';
                            borderClass = 'border-2 border-red-400';
                            textColorClass = 'text-red-700';
                          } else if (diffFromBest >= 5) {
                            bgColorClass = 'bg-orange-50';
                            borderClass = 'border-2 border-orange-400';
                            textColorClass = 'text-orange-700';
                          } else {
                            bgColorClass = 'bg-green-50';
                            borderClass = 'border-2 border-green-300';
                            textColorClass = 'text-green-700';
                          }
                        }
                        
                        // Individual site cells are NOT clickable - no modal popup
                        return (
                          <td 
                            key={timeIndex} 
                            className={`px-4 py-4 whitespace-nowrap text-sm text-center ${bgColorClass} ${borderClass}`}
                          >
                            <span className={`font-semibold ${textColorClass}`}>
                              {percentValue !== null && percentValue !== undefined ? `${percentValue}%` : 'N/A'}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                    );
                  });
                })()
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for showing inverter performance details */}
      {modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end md:items-center justify-center z-50 p-0 md:p-4" onClick={() => setModalData(null)}>
          <div
            className={`rounded-t-2xl md:rounded-lg shadow-xl p-4 md:p-6 max-w-4xl w-full md:mx-4 max-h-[92vh] md:max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile drag handle */}
            <div className="md:hidden flex justify-center mb-2">
              <div className={`w-12 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg md:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Inverter Performance Details
              </h3>
              <button
                onClick={() => setModalData(null)}
                className={`text-3xl leading-none w-9 h-9 flex items-center justify-center rounded-full transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <span className="font-semibold">Site:</span> {modalData.siteName.replace(/^Site \d+: /, '')}
              </p>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <span className="font-semibold">Time:</span> {modalData.time}
              </p>
              {modalData.p5 !== null && modalData.p10 !== null && modalData.p5 !== undefined && modalData.p10 !== undefined && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-semibold">Percentiles:</span> 5th = {modalData.p5?.toFixed(2)}%, 10th = {modalData.p10?.toFixed(2)}%
                </p>
              )}
            </div>

            <div className="space-y-4">
              {modalData.loading && (
                <div className="flex flex-col items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-4"></div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Loading inverter details…
                  </p>
                </div>
              )}
              {modalData.loadFailed && (
                <div className={`rounded-lg p-3 border ${darkMode ? 'bg-red-900 bg-opacity-30 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  <p className="text-sm">Couldn&apos;t load the inverter details for this hour. Please try again.</p>
                </div>
              )}
              {modalData.bestInverter && modalData.maxPercent !== null && modalData.maxPercent !== undefined && (
                <div className={`pb-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Best Performing Inverter:
                  </p>
                  <p className={`text-base font-semibold p-2 rounded border-2 ${darkMode ? 'text-green-300 bg-green-900 bg-opacity-30 border-green-600' : 'text-green-700 bg-green-50 border-green-400'}`}>
                    Inverter {modalData.bestInverter.inverter}: {modalData.maxPercent?.toFixed(2) || 'N/A'}%
                  </p>
                </div>
              )}
              {modalData.maxInverter && !modalData.bestInverter && modalData.maxPercent !== null && modalData.maxPercent !== undefined && (
                <div className={`pb-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Best Performing Inverter:
                  </p>
                  <p className={`text-base font-semibold ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                    {modalData.maxInverter}: {modalData.maxPercent?.toFixed(2) || 'N/A'}%
                  </p>
                </div>
              )}

              {modalData.showCapacityQuotation && (
                <div className={`pb-3 border-b rounded-lg p-3 ${darkMode ? 'bg-blue-900 bg-opacity-30 border-blue-700 text-blue-200' : 'bg-blue-50 border border-blue-200 text-blue-800'}`}>
                  <p className="text-sm italic">
                    &ldquo;All inverters appear red compared to the best because the best-performing inverter has lower capacity; the other inverters have higher capacity. Percentages are relative to each inverter&apos;s own capacity, so a smaller inverter can show a higher utilization.&rdquo;
                  </p>
                </div>
              )}

              {(() => {
                const worstList = modalData.worst2Performing || modalData.least2Performing || [];
                const maxP = modalData.maxPercent;
                const hasUnderperformers =
                  maxP !== null && maxP !== undefined &&
                  (modalData.allInverters || []).some(inv => {
                    const p = inv?.percent;
                    if (p === null || p === undefined) return false;
                    return (maxP - p) >= 5 && (maxP - p) > 0;
                  });
                return hasUnderperformers && worstList.length > 0;
              })() && (
                <div className={`pb-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Worst Performing Inverters (these are dragging the site down):
                  </p>
                  <div className="space-y-2">
                    {(modalData.worst2Performing || modalData.least2Performing).map((inv, idx) => {
                      const difference = (modalData.maxPercent || 0) - (inv.percent || 0);
                      const isRed = difference >= 10;
                      const isOrange = difference >= 5 && difference < 10;
                      const bg = darkMode
                        ? (isRed ? 'bg-red-900 bg-opacity-30 border-red-600 text-red-300' : (isOrange ? 'bg-orange-900 bg-opacity-30 border-orange-600 text-orange-300' : 'bg-gray-700 border-gray-600 text-gray-200'))
                        : (isRed ? 'bg-red-50 border-red-400 text-red-700' : (isOrange ? 'bg-orange-50 border-orange-400 text-orange-700' : 'bg-gray-50 border-gray-300 text-gray-700'));
                      return (
                        <div key={idx} className={`pb-2 last:pb-0 p-3 rounded border-2 ${bg}`}>
                          <p className="text-base font-semibold">
                            Inverter {inv.inverter}: {inv.percent !== null && inv.percent !== undefined ? inv.percent.toFixed(2) : 'N/A'}%
                          </p>
                          <p className={`text-xs mt-0.5 ${darkMode ? 'opacity-90' : 'opacity-80'}`}>
                            Underperforming by: <span className="font-semibold">{difference.toFixed(2)}%</span> compared to best
                          </p>
                          {inv.fullName && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {inv.fullName}{inv.capacity_kw ? ` • ${inv.capacity_kw} kW` : ''}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={modalData.loading || modalData.loadFailed ? 'hidden' : ''}>
                <div className="flex items-center justify-between mb-2">
                  <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    All Inverter Performance (sorted by performance)
                  </p>
                  {/* Compact legend so the user knows what the colors mean */}
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="flex items-center gap-1">
                      <span className={`w-2.5 h-2.5 rounded-full ${darkMode ? 'bg-green-500' : 'bg-green-500'}`}></span>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Best</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Healthy (&lt;5%)</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>5–10%</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>≥10%</span>
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
                  {(() => {
                    const sortedInverters = [...modalData.allInverters].sort((a, b) => b.percent - a.percent);

                    return sortedInverters.map((inv, idx) => {
                      const worst2List = modalData.worst2Performing || modalData.least2Performing || [];
                      const isWorst2 = worst2List.some(worst => worst.inverter === inv.inverter);
                      const isBest = modalData.bestInverter && inv.inverter === modalData.bestInverter.inverter;

                      const maxPercent = modalData.maxPercent || 0;
                      const invPercent = inv.percent || 0;
                      const differenceFromBest = maxPercent - invPercent;

                      // Bucket: best / healthy / orange / red
                      let bucket = 'healthy';
                      if (isBest) bucket = 'best';
                      else if (differenceFromBest >= 10) bucket = 'red';
                      else if (differenceFromBest >= 5) bucket = 'orange';

                      // Every card gets a solid border + background — no transparent cards
                      const styles = {
                        best: {
                          card: darkMode ? 'bg-green-900 bg-opacity-30 border-green-500' : 'bg-green-50 border-green-500',
                          dot: 'bg-green-500',
                          rank: darkMode ? 'bg-green-700 text-green-100' : 'bg-green-500 text-white',
                          value: darkMode ? 'text-green-300' : 'text-green-700',
                          label: darkMode ? 'text-green-400' : 'text-green-700',
                          diff: darkMode ? 'text-green-400' : 'text-green-600',
                        },
                        healthy: {
                          card: darkMode ? 'bg-gray-700 border-emerald-700' : 'bg-emerald-50 border-emerald-300',
                          dot: 'bg-emerald-400',
                          rank: darkMode ? 'bg-emerald-800 text-emerald-200' : 'bg-emerald-200 text-emerald-800',
                          value: darkMode ? 'text-emerald-200' : 'text-emerald-800',
                          label: darkMode ? 'text-gray-300' : 'text-gray-700',
                          diff: darkMode ? 'text-gray-400' : 'text-gray-500',
                        },
                        orange: {
                          card: darkMode ? 'bg-orange-900 bg-opacity-30 border-orange-500' : 'bg-orange-50 border-orange-400',
                          dot: 'bg-orange-500',
                          rank: darkMode ? 'bg-orange-800 text-orange-100' : 'bg-orange-500 text-white',
                          value: darkMode ? 'text-orange-200' : 'text-orange-700',
                          label: darkMode ? 'text-orange-300' : 'text-orange-700',
                          diff: darkMode ? 'text-orange-300' : 'text-orange-600',
                        },
                        red: {
                          card: darkMode ? 'bg-red-900 bg-opacity-30 border-red-500' : 'bg-red-50 border-red-500',
                          dot: 'bg-red-500',
                          rank: darkMode ? 'bg-red-800 text-red-100' : 'bg-red-500 text-white',
                          value: darkMode ? 'text-red-200' : 'text-red-700',
                          label: darkMode ? 'text-red-300' : 'text-red-700',
                          diff: darkMode ? 'text-red-300' : 'text-red-600',
                        },
                      };
                      const s = styles[bucket];
                      const worstRing = isWorst2 && !isBest ? (darkMode ? ' ring-2 ring-offset-1 ring-offset-gray-800 ring-gray-200' : ' ring-2 ring-offset-1 ring-gray-700') : '';

                      return (
                        <div key={idx} className={`flex items-center gap-3 p-2.5 rounded-lg border-2 ${s.card}${worstRing}`}>
                          {/* Rank badge (1st = best) */}
                          <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${s.rank}`}>
                            {idx + 1}
                          </div>
                          {/* Status dot */}
                          <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${s.dot}`}></span>
                          {/* Inverter info */}
                          <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                            <span className={`text-sm font-semibold ${s.label}`}>
                              Inverter {inv.inverter}
                            </span>
                            <span className={`text-sm font-bold ${s.value}`}>
                              {inv.percent !== null && inv.percent !== undefined ? inv.percent.toFixed(2) : 'N/A'}%
                              {!isBest && differenceFromBest > 0 && (
                                <span className={`text-[11px] ml-1.5 font-medium ${s.diff}`}>
                                  (−{differenceFromBest.toFixed(2)})
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setModalData(null)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for showing weighted average calculation */}
      {weightedAvgModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setWeightedAvgModalData(null)}>
          <div className={`rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Weighted Average Calculation
              </h3>
              <button
                onClick={() => setWeightedAvgModalData(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">Site:</span> {weightedAvgModalData.siteName}
              </p>
              <p className="text-sm text-gray-600 mb-4">
                <span className="font-semibold">Formula:</span> Weighted Average = Σ(Health × Power) / Σ(Power)
              </p>
            </div>

            {/* Calculation breakdown table */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Hour-by-Hour Breakdown (7 AM - 6 PM):</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Hour</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-700 uppercase">Health (%)</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-700 uppercase">Power (kW)</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-700 uppercase">Health × Power</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {weightedAvgModalData.calculationBreakdown.map((row, idx) => (
                      <tr key={idx} className={row.health === null || row.power === 0 ? 'bg-gray-50' : ''}>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-900 font-medium">{row.hour}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-center text-gray-700">
                          {row.health !== null ? `${row.health}%` : 'N/A'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center text-gray-700">
                          {row.power > 0 ? row.power.toFixed(2) : '0.00'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center text-gray-700 font-medium">
                          {row.healthPower > 0 ? row.healthPower.toFixed(2) : '0.00'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Σ(Health × Power):</span>
                <span className="text-sm font-semibold text-gray-900">{weightedAvgModalData.sumHealthPower.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Σ(Power):</span>
                <span className="text-sm font-semibold text-gray-900">{weightedAvgModalData.sumPower.toFixed(2)} kW</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-base font-bold text-gray-900">Weighted Average Health:</span>
                <span className="text-lg font-bold text-green-600">
                  {weightedAvgModalData.weightedAvg !== null 
                    ? `${weightedAvgModalData.weightedAvg}%` 
                    : 'N/A'}
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setWeightedAvgModalData(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EnhancedHealthDashboard;

